import React, { useState, useEffect, useRef } from 'react';
import * as Layout from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import config from '../../../constants/config';
import { PAYMENT_METHODS } from '../../../constants/paymentMethods';
import API from '../../../api/Api';
import OrderStatus from './OrderStatus';
import { useLanguage } from '../../../contexts/LanguageContext';
import localized from '../../../utils/localized';

// Per-checkout idempotency key. Generated once for an order attempt and REUSED on every retry, so a
// double-tap or a network hang (request sent, response lost) can't create a second order — the
// server dedupes on this key. Cleared after a successful placement so the next order gets a fresh one.
const newIdemKey = () => (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

// localStorage key for the guest's most recent order on this venue, so they can re-open the
// tracker after a refresh (the token is the only thing that lets them follow status without a login).
const lastOrderKey = (venueId) => `mb_order_${venueId}`;
const RECENT_MS = 6 * 60 * 60 * 1000; // surface the "track recent order" entry for 6h

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Mirror of node/services/tax.service.computeBill — shows the bill breakdown before checkout.
// Display only; the server recomputes authoritatively when the order is placed.
const computeBill = (config, subtotal) => {
    const sub = round2(subtotal);
    const mode = config?.tax_mode || 'none';
    const rate = Number(config?.tax_rate) || 0;
    let tax = 0;
    if (rate > 0 && mode === 'inclusive') tax = round2(sub - sub / (1 + rate / 100));
    else if (rate > 0 && mode === 'exclusive') tax = round2(sub * rate / 100);
    const lines = (config?.charges || []).map((c) => ({
        name: c.name,
        amount: round2(c.type === 'fixed' ? Number(c.value) : sub * Number(c.value) / 100),
    }));
    const chargesTotal = round2(lines.reduce((s, c) => s + c.amount, 0));
    const total = round2(sub + (mode === 'exclusive' ? tax : 0) + chargesTotal);
    return { sub, mode, rate, label: config?.tax_label || 'Tax', tax, lines, total };
};

const Cart = ({ show, onHide, cart, setCart, venueId, menuId, currencySymbol, lockedTable, loyalty }) => {
    // Venue's display currency, passed down from MenuPage (venue.currency_symbol). Fall back to the
    // platform default so the cart never renders an empty symbol if it's ever mounted without it.
    const sym = currencySymbol || config.currencySymbol;
    const { lang } = useLanguage();
    const { t } = useTranslation();
    const [name, setName] = useState('');
    // (B6d) Mobile — the loyalty identity; remembered on the device to prefill next visit.
    const [mobile, setMobile] = useState(() => { try { return localStorage.getItem('mb_mobile') || ''; } catch (e) { return ''; } });
    const [tableNo, setTableNo] = useState(lockedTable || '');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [payEnabled, setPayEnabled] = useState(false);
    const [trackToken, setTrackToken] = useState('');   // token of the order being tracked right now
    const [recentToken, setRecentToken] = useState('');  // a previously-placed order still worth tracking
    const idemKeyRef = useRef(null);  // stable across retries of the SAME order; reset after success
    const [taxConfig, setTaxConfig] = useState(null);  // venue tax + charges, for the bill breakdown

    // Check whether this venue accepts online payment (Stripe configured)
    useEffect(() => {
        API.getData('payment/config')
            .then(res => setPayEnabled(!!res.data?.data?.enabled))
            .catch(() => setPayEnabled(false));
    }, []);

    // Load the venue's tax + charges so the cart can show the bill breakdown before checkout.
    useEffect(() => {
        if (!venueId) return;
        API.getData(`venues/tax-config/${venueId}`)
            .then(res => setTaxConfig(res.data?.data || null))
            .catch(() => setTaxConfig(null));
    }, [venueId]);

    // On open, surface a "track your recent order" entry if the guest placed one in the last few hours.
    useEffect(() => {
        if (!show) return;
        try {
            const raw = localStorage.getItem(lastOrderKey(venueId));
            if (!raw) { setRecentToken(''); return; }
            const saved = JSON.parse(raw);
            if (saved?.token && saved?.placedAt && (Date.now() - saved.placedAt) < RECENT_MS) {
                setRecentToken(saved.token);
            } else {
                setRecentToken('');
            }
        } catch (e) { setRecentToken(''); }
    }, [show, venueId]);

    const total = cart.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.qty) || 0), 0);
    const bill = computeBill(taxConfig, total);  // subtotal + tax + charges breakdown for display

    // Use _key (fingerprint) when available; fall back to id for old carts loaded from localStorage.
    const updateQty = (key, delta) => {
        setCart(prev => prev
            .map(i => (i._key || String(i.id)) === key ? { ...i, qty: i.qty + delta } : i)
            .filter(i => i.qty > 0)
        );
    };

    const placeOrder = async () => {
        if (!name.trim()) { setErrorMsg(t('cart.error_name')); return; }
        // Reuse the key if this is a retry of a failed attempt; mint a fresh one otherwise.
        if (!idemKeyRef.current) idemKeyRef.current = newIdemKey();
        setSubmitting(true);
        setErrorMsg('');
        try {
            const cartItems = cart.map(i => ({
                id: i.id,
                price: parseFloat(i.price),
                quantity: i.qty,
                pay_amount: parseFloat(i.price) * i.qty,
                // selectedModifiers may be absent on old localStorage carts — guard it
                modifiers: (i.selectedModifiers || []).map(m => m.option_id),
            }));
            const res = await API.postData('order/create', {
                venue_id: venueId,
                menu_id: menuId,
                customer_name: name,
                customer_mobile: mobile.trim() || null,
                table_no: tableNo,
                notes: notes.trim(),
                payment_method: payEnabled ? PAYMENT_METHODS.ONLINE : PAYMENT_METHODS.AT_VENUE,
                item_count: cart.reduce((s, i) => s + i.qty, 0),
                total_price: total,
                cart_item: cartItems,
                idempotency_key: idemKeyRef.current
            });
            // The API returns HTTP 200 even on a business rejection (e.g. venue paused ordering),
            // signalling failure via { status:false, message }. Surface that real message instead
            // of falsely showing "order placed".
            if (!res.data?.status) {
                setErrorMsg(res.data?.message || t('cart.error_place'));
                setSubmitting(false);
                return;
            }
            const orderId = res.data?.data?.order_id;
            const token = res.data?.data?.track_token;

            // Persist the token so the guest can re-open the live tracker after a page refresh.
            if (token) {
                try {
                    localStorage.setItem(lastOrderKey(venueId), JSON.stringify({ token, placedAt: Date.now() }));
                    if (mobile.trim()) localStorage.setItem('mb_mobile', mobile.trim()); // remember for loyalty prefill
                } catch (e) { /* private mode / quota — tracking just won't survive a refresh */ }
            }

            // If online payment is enabled, redirect to Stripe Checkout to pay now.
            if (payEnabled && orderId) {
                try {
                    const co = await API.postData('payment/create-checkout', { order_id: orderId });
                    const url = co.data?.data?.checkout_url;
                    if (url) { window.location.href = url; return; }
                } catch (e) {
                    // Fall through to "order placed, pay at venue" if checkout fails.
                }
            }
            setTrackToken(token || '');
            setOrderPlaced(true);
            setCart([]);
            setNotes('');
            idemKeyRef.current = null;  // order succeeded — the next order gets a fresh key
        } catch (e) {
            // Distinguish a dropped connection from a real server error: axios network failures
            // arrive with no `response`. The cart is NOT cleared here, so the order is never lost —
            // the guest can simply tap Place Order again once they're back online.
            if (!navigator.onLine || !e.response) {
                setErrorMsg(t('cart.error_offline'));
            } else {
                setErrorMsg(t('cart.error_place'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (orderPlaced) setOrderPlaced(false);
        setTrackToken('');
        onHide();
    };

    const recentTrackButton = recentToken ? (
        <button className='mb-cart-track-recent' onClick={() => setTrackToken(recentToken)}>
            {t('cart.track_recent')}
        </button>
    ) : null;

    return (
        <Layout.Offcanvas className='mb-cart' show={show} onHide={handleClose} placement='bottom' style={{ maxHeight: '85vh' }}>
            <Layout.Offcanvas.Header closeButton>
                <Layout.Offcanvas.Title>{trackToken ? t('order.status_title') : t('cart.title')}</Layout.Offcanvas.Title>
            </Layout.Offcanvas.Header>
            <Layout.Offcanvas.Body>
                {trackToken ? (
                    <OrderStatus token={trackToken} onClose={handleClose} currencySymbol={sym} />
                ) : cart.length === 0 ? (
                    <>
                        <div className='mb-cart-empty'>{t('cart.empty')}</div>
                        {recentTrackButton}
                    </>
                ) : (
                    <>
                        {recentTrackButton}
                        {cart.map(item => {
                            // Use _key fingerprint when available; fall back to id for old carts
                            const key = item._key || String(item.id);
                            // Build modifier summary string: "Large · Oat milk" etc.
                            const modSummary = (item.selectedModifiers && item.selectedModifiers.length > 0)
                                ? item.selectedModifiers.map(m => m.name).join(' · ')
                                : '';
                            return (
                                <div key={key} className='mb-cart-item'>
                                    <div className='mb-cart-item-info'>
                                        <p className='mb-cart-item-name'>{localized(item, lang)}</p>
                                        {modSummary && (
                                            <p className='mb-cart-item-mods'>{modSummary}</p>
                                        )}
                                        <span className='mb-cart-item-each'>{sym}{parseFloat(item.price).toFixed(2)} {t('common.each')}</span>
                                    </div>
                                    <div className='mb-cart-stepper'>
                                        <button className='mb-qty-btn' aria-label={t('cart.decrease_qty')} onClick={() => updateQty(key, -1)}>−</button>
                                        <span className='mb-cart-qty'>{item.qty}</span>
                                        <button className='mb-qty-btn' aria-label={t('cart.increase_qty')} onClick={() => updateQty(key, +1)}>+</button>
                                        <span className='mb-cart-line-total'>{sym}{((parseFloat(item.price) || 0) * (parseInt(item.qty) || 0)).toFixed(2)}</span>
                                    </div>
                                </div>
                            );
                        })}
                        {(bill.mode !== 'none' || bill.lines.length > 0) && (
                            <div style={{ margin: '4px 0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: '#6b6b60', padding: '2px 0' }}>
                                    <span>{t('cart.subtotal')}</span><span>{sym}{bill.sub.toFixed(2)}</span>
                                </div>
                                {bill.mode === 'exclusive' && bill.tax > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: '#6b6b60', padding: '2px 0' }}>
                                        <span>{bill.label} ({bill.rate}%)</span><span>{sym}{bill.tax.toFixed(2)}</span>
                                    </div>
                                )}
                                {bill.lines.map((l, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: '#6b6b60', padding: '2px 0' }}>
                                        <span>{l.name}</span><span>{sym}{l.amount.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className='mb-cart-total-row'>
                            <span>{t('cart.total')}</span>
                            <span className='mb-cart-grand'>{sym}{bill.total.toFixed(2)}</span>
                        </div>
                        {bill.mode === 'inclusive' && bill.tax > 0 && (
                            <div style={{ fontSize: '0.76rem', color: '#8a8a80', textAlign: 'right', marginTop: '-2px' }}>
                                {t('cart.incl_tax', { label: bill.label, rate: bill.rate, sym, amount: bill.tax.toFixed(2) })}
                            </div>
                        )}
                        <label htmlFor='mb-cart-name' className='visually-hidden'>{t('cart.name_label')}</label>
                        <input
                            id='mb-cart-name'
                            className='mb-cart-input'
                            placeholder={t('cart.name_placeholder')}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            autoComplete='name'
                            aria-required='true'
                        />
                        {/* (B6d) Loyalty: teaser + mobile field (the reward identity). */}
                        {loyalty && loyalty.reward && (
                            <div className='mb-cart-loyalty'>
                                🎟️ {loyalty.model === 'stamps'
                                    ? t('cart.loyalty_stamps', { n: loyalty.target, reward: loyalty.reward, defaultValue: `Buy ${loyalty.target}, get ${loyalty.reward}` })
                                    : t('cart.loyalty_spend', { amount: `${sym}${loyalty.target}`, reward: loyalty.reward, defaultValue: `Spend ${sym}${loyalty.target}, get ${loyalty.reward}` })}
                            </div>
                        )}
                        <label htmlFor='mb-cart-mobile' className='visually-hidden'>{t('cart.mobile_label', 'Mobile')}</label>
                        <input
                            id='mb-cart-mobile'
                            className='mb-cart-input'
                            type='tel'
                            placeholder={loyalty ? t('cart.mobile_rewards_placeholder', 'Mobile (to collect rewards)') : t('cart.mobile_placeholder', 'Mobile (optional)')}
                            value={mobile}
                            onChange={e => setMobile(e.target.value)}
                            autoComplete='tel'
                        />
                        {lockedTable ? (
                            // Table-QR mode: the table came from the scanned QR — show it locked, don't let it be edited.
                            <div className='mb-cart-input' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f6f5f2', color: '#3a352f' }}>
                                <span>{t('cart.table_locked', { table: lockedTable })}</span>
                                <span aria-hidden='true' title={t('cart.table_locked_hint')}>🔒</span>
                            </div>
                        ) : (
                            <>
                                <label htmlFor='mb-cart-table' className='visually-hidden'>{t('cart.table_label')}</label>
                                <input
                                    id='mb-cart-table'
                                    className='mb-cart-input'
                                    placeholder={t('cart.table_placeholder')}
                                    value={tableNo}
                                    onChange={e => setTableNo(e.target.value)}
                                    inputMode='numeric'
                                    autoComplete='off'
                                />
                            </>
                        )}
                        <label htmlFor='mb-cart-notes' className='visually-hidden'>{t('cart.notes_label')}</label>
                        <textarea
                            id='mb-cart-notes'
                            className='mb-cart-input mb-cart-notes'
                            placeholder={t('cart.notes_placeholder')}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            rows={2}
                            maxLength={255}
                            autoComplete='off'
                        />
                        {errorMsg && <p className='mb-cart-error'>{errorMsg}</p>}
                        <button
                            className='mb-cart-submit'
                            onClick={placeOrder}
                            disabled={submitting || cart.length === 0}
                        >
                            {submitting
                                ? t('cart.placing_order')
                                : t(payEnabled ? 'cart.pay_now' : 'cart.place_order', { sym, total: bill.total.toFixed(2) })}
                        </button>
                    </>
                )}
            </Layout.Offcanvas.Body>
        </Layout.Offcanvas>
    );
};

export default Cart;
